import { formKitSchemaOverrider } from "@/composables/useXsdToFormKit";
import type { FormKitNode } from "@formkit/core";

function addressLabel(node: FormKitNode, index: number) {
	const nodeObjectOrArray = node.value as any;
	if (!nodeObjectOrArray) {
		return;
	}

	const nodeObject = Array.isArray(nodeObjectOrArray)
		? nodeObjectOrArray[index]
		: nodeObjectOrArray;
	if (!nodeObject) {
		return;
	}

	if (nodeObject.StraatNaam) {
		let label = nodeObject.StraatNaam;

		if (nodeObject.HuisNr) {
			label += ` ${nodeObject.HuisNr}`;

			if (nodeObject.HuisNrToevoeging) {
				label += nodeObject.HuisNrToevoeging;
			}
		}

		return label;
	}

	if (nodeObject.Postcode && nodeObject.PlaatsNaam) {
		return `${nodeObject.Postcode} ${nodeObject.PlaatsNaam}`;
	}

	return;
}

export function useLoanApplicationFormOverrider() {
	function labelFromPartijNAWData(partijNAWData?: any) {
		if (!partijNAWData) {
			return;
		}

		const labelParts: string[] = [];
		if (partijNAWData.VoorNamen) {
			labelParts.push(partijNAWData.VoorNamen);
		} else if (partijNAWData.VoorLetters) {
			labelParts.push(partijNAWData.VoorLetters);
		}

		if (partijNAWData.GeboorteTussenvoegsels) {
			labelParts.push(partijNAWData.GeboorteTussenvoegsels);
		}

		if (partijNAWData.GeboorteAchternaam) {
			labelParts.push(partijNAWData.GeboorteAchternaam);
		}

		if (labelParts.length === 0) {
			return;
		}

		return labelParts.join(" ");
	}

	const overrider = formKitSchemaOverrider({
		byName: {
			Hypotheekgever: {},
			VakantieToeslag: {
				label: "Vakantietoeslag",
			},
			TeVerkopenJN: {
				label: "Wordt het huidige object verkocht?",
			},
			VerkochtJN: {
				label: "Is het huidige object al verkocht?",
			},
			VerkoopakteJN: {
				label: "Is er een getekende verkoopakte van het huidige object?",
			},
			VerkoopPrijs: {
				label: "Verkoopprijs",
			},
			VerkoopKosten: {
				label: "Verkoopkosten",
			},
			VerkoopDt: {
				label: "Verkoopdatum",
			},
			OntbindendeVoorwaardenDt: {
				label: "Datum ontbindende voorwaarden",
			},
			OverbruggingJN: {
				label: "Wordt er een overbruggingslening aangevraagd?",
			},
			OverbruggingsBedrag: {
				label: "Overbruggingsbedrag",
			},
			HuidigeFinancieringJN: {
				label: "Is het huidig onderpand belast met een hypotheek?",
			},
			AanvragerBestaandeHypotheek: {
				label: "Aanvrager bestaande hypotheek",
			},
			HuidigeFinanciering: {
				label: "Huidige hypotheek",
			},
			VerkoopOnderVoorwaardenJN: {
				label: "Is de verkoop onder voorwaarden?",
			},
			Volgnummer: {
				hidden: true,
			},
			VoorLetters: {
				label: "Voorletters",
			},
			VoorNamen: {
				label: "Voornamen",
			},
			GeboorteAchternaam: {
				label: "Achternaam",
			},
			GeboorteTussenvoegsels: {
				label: "Tussenvoegsels",
			},
			GeboorteDt: {
				label: "Geboortedatum",
				props: {
					twoDigitYearCentury: "past",
				},
			},
			GeboorteLand: {
				label: "Geboorteland",
			},
			GeboortePlaats: {
				label: "Geboorteplaats",
			},
			PlaatsNaam: {
				label: "Plaatsnaam",
			},
			BedrijfsNaam: {
				label: "Bedrijfsnaam",
			},
			AFMRegistratieNr: {
				label: "AFM registratienummer",
			},
			CorrespondentieVoornaam: {
				label: "Voornaam voor correspondentie",
			},
			CorrespondentieNaam: {
				label: "Achternaam voor correspondentie",
			},
			CorrespondentieTussenvoegsels: {
				label: "Tussenvoegsels voor correspondentie",
			},
			StraatNaam: {
				label: "Straat",
			},
			HuisNrToevoeging: {
				label: "Toevoeging",
			},
			HuwelijkOntbondenJN: {
				label: "Samenlevingsvorm ontbonden?",
			},
			HuwelijkOntbondenDt: {
				label: "Datum samenlevingsvorm ontbonden",
			},
			TelefoonNrPrive: {
				label: "Telefoonnummer privé",
			},
			TelefoonNrMobiel: {
				label: "Telefoonnummer mobiel",
			},
			TelefoonNrWerk: {
				label: "Telefoonnummer werk",
			},
			SoortOntbonden: {
				label: "Welke samenlevingsvorm is ontbonden?",
			},
			WeduweWeduwnaarJN: {
				label: "Is de aanvrager een weduwe/weduwnaar?",
			},
			AanvragerIsStarterJN: {
				label: "Is dit de eerste woninghypotheek van de aanvrager?",
			},
			BurgerlijkeStaatDt: {
				label: "Datum burgerlijke staat",
			},
			AfwijkendKlantProfielJN: {
				label: "Afwijkend klantprofiel?",
			},
			KlantSegmentMij: {
				label: "Klantsegment",
			},
			FiscaalInwonerVanNederlandJN: {
				label: "Fiscaal inwoner van Nederland?",
			},
			UitsluitendOfMedeFiscaalInwonerBuitenNedJN: {
				label: "Volledig of mede fiscaal inwoner buiten Nederland?",
			},
			Dataleverancier: {
				hidden: true,
			},
			ExterneIdentificatie: {
				hidden: true,
			},
			HoofdelijkAansprakelijkJN: {
				label: "Hoofdelijk aansprakelijk?",
			},
			MedeSchuldenaarJN: {
				label: "Medeschuldenaar?",
			},
			// TODO: fill this automatically somehow
			// SoortPartij: {
			//   hidden: true,
			// },
			ObjectSoort: {
				label: "Objectsoort",
			},
			BijzObjectSoort: {
				label: "Bijzondere objectsoort",
			},
			GarageJN: {
				label: "Is er een garage aanwezig?",
			},
			RecreatieJN: {
				label: "Is het een recreatiewoning?",
			},
			BouwPlanNr: {
				label: "Bouwplannummer",
			},
			EigenBewoningJN: {
				label: "Gaat de aanvrager zelf wonen in het object?",
			},
			CodeVerhuur: {
				label: "Soort verhuur",
			},
			HuurovereenkomstJN: {
				label: "Is er een actieve huurovereenkomst?",
			},
			HuurovereenkomstIngangsDt: {
				label: "Ingangsdatum huurovereenkomst",
			},
			VerhuurPct: {
				label: "Percentage verhuur",
			},
			VerhuurEindDt: {
				label: "Einddatum verhuur",
			},
			AfkoopExPartner: {
				label: "Afkoopbedrag ex-partner",
			},
			AfkoopErgenamen: {
				label: "Afkoopbedrag erfgenamen",
			},
			VruchtgebruikJN: {
				label: "Is er sprake van vruchtgebruik?",
			},
			ErfpachtJN: {
				label: "Is er sprake van erfpacht?",
			},
			AfkoopErfpachtJN: {
				label: "Is de erfpacht afgekocht?",
			},
			ErfpachtAfkoopsom: {
				label: "Voor welk bedrag kan de erfpacht worden afgekocht?",
			},
			ErfpachtMeeFinancierenJN: {
				label: "Wordt de erfpacht meegefinancierd?",
			},
			ErfpachtCanonInVerledenAfgekochtJN: {
				label: "Is de erfpachtcanon in het verleden afgekocht?",
			},
			ErfpachtEeuwigdurendJN: {
				label: "Is de erfpacht eeuwigdurend?",
			},
			ErfpachtCanonEeuwigDurendAfgekochtJN: {
				label: "Is de erfpachtcanon eeuwigdurend afgekocht?",
			},
			ErfpachtEindDt: {
				label: "Einddatum erfpacht",
			},
			ErfpachtCanon: {
				label: "Erfpachtcanon",
			},
			ErfpachtCanonAfgekochtTotDt: {
				label: "Tot wanneer zijn de betalingen van de erfpachtcanon afgekocht?",
			},
			ErfpachtIngangsDt: {
				label: "Ingangsdatum erfpacht",
			},
			BouwJaar: {
				label: "Bouwjaar",
			},
			AppartementsrechtJN: {
				label: "Is er een appartementsrecht van kracht op het onderpand?",
			},
			HoofdVerblijfJN: {
				label: "Wordt het object het hoofdverblijf van de aanvrager?",
			},
			TaxatieAanwezigJN: {
				label: "Is er een taxatierapport aanwezig?",
			},
			BronWaardebepaling: {
				label: "Hoe wordt de waarde bepaald?",
			},
			TaxatieDt: {
				label: "Taxatiedatum",
			},
			KostenTaxatie: {
				label: "Taxatiekosten",
			},
			AfkoopsomToekOhd: {
				label: "Afkoopsom toekomstig onderhoud",
			},
			OnderhoudsToestand: {
				label: "Onderhoudstoestand",
			},
			MarktWaarde: {
				label: "Marktwaarde",
			},
			KostenKoperJN: {
				label: "Is er sprake van kosten koper?",
			},
			HuidigeHuurderJN: {
				label: "Is de koper de huidige huurder?",
			},
			WOZWaarde: {
				label: "WOZ-waarde",
			},
			WOZWaardeTaxatieDt: {
				label: "WOZ-waarde taxatiedatum",
			},
			AchterstalligOnderhoud: {
				label: "De waarde van achterstallig onderhoud",
			},
			VerbouwingsKosten: {
				label: "Verbouwingskosten",
			},
			VerduurzamingBesprokenJN: {
				label: "Is verduurzaming besproken?",
			},
			VerduurzamingToepassen: {
				label: "Wordt verduurzaming toegepast?",
			},
			BedragKwaliteitsverbeteringEBV: {
				label: "Bedrag kwaliteitsverbetering EBV",
			},
			BedragKwaliteitsverbeteringEBB: {
				label: "Bedrag kwaliteitsverbetering EBB",
			},
			MarktWaardeNaVerb: {
				label: "Marktwaarde na verbouwing",
			},
			VerbouwingsDepot: {
				label: "Verbouwingsdepot",
			},
			AanneemSom: {
				label: "Aanneemsom",
			},
			GrondReedsInBezitJN: {
				label: "Is de grond al in het bezit van de aanvrager?",
			},
			RenteVerlies: {
				label: "Het bedrag dat aan renteverlies wordt geleden",
			},
			HypotheekInclRenteVerliesJN: {
				label: "Dient renteverlies meegefinancierd te worden?",
			},
			OpleveringsDt: {
				label: "Opleveringsdatum",
			},
			BouwRente: {
				label: "Bouwrente",
			},
			ServiceKosten: {
				label: "Servicekosten",
			},
			KostenLeveringsAkteSpec: {
				label: "Kosten voor de leveringsakte",
			},
			MakelaarsKostenSpec: {
				label: "Aankopende makelaarskosten",
			},
			OverdrachtsBelastingSpec: {
				label: "Overdrachtsbelasting",
			},
			VerkoopOnderVoorwaardenConstructie: {
				label: "Verkoopconstructie",
				sorted: true,
			},
			ErfpachtOfKortingsConstructieAnders: {
				label: "Erfpacht- of kortingsconstructie",
			},
			VerkoopOnderVoorwaardenConstructieOmschr: {
				label: "Omschrijving verkoopconstructie",
			},
			OorspronkelijkBindendAanbod: {
				label: "Wanneer is het bindend aanbod uitgebracht?",
			},
			VolledigeAfbetalingKortingsdeel: {
				label: "Het volledige bedrag om van het kortingsdeel af te komen",
			},
			AflossenJN: {
				label: "Wordt de financiering afgelost?",
			},
			EnergieKlasse: {
				label: "Energielabel",
			},
			EnergieklasseAfgifteDt: {
				label: "Datum afgifte energielabel",
			},
			PremieOpstalverzekeringPerJaar: {
				label: "Premie opstalverzekering per jaar",
			},
			CodeNotaris: {
				label: "Welke rol vervult deze notaris?",
			},
			SoortAdvies: {
				label: "Wat voor advies wordt er gegeven?",
			},
			HypotheekBedrag: {
				label: "Hypotheekbedrag",
			},
			RangOrde: {
				label: "Rangorde",
			},
			BestaandeHypotheekNr: {
				label: "Huidige hypotheeknummer",
			},
			ProductNaam: {
				label: "Productnaam",
			},
			CodeLeningMij: {
				label: "Productcode van de lening",
			},
			PasseerDt: {
				label: "Passeerdatum",
			},
			IngebrachtEigenMiddelen: {
				label: "Ingebrachte eigen middelen",
			},
			BankGarantieBedrag: {
				label: "Bankgarantiebedrag",
			},
			BankGarantieKosten: {
				label: "Bankgarantiekosten",
			},
			BespreekZaakJN: {
				label: "Is de aanvraag besproken met de ontvangende partij?",
			},
			BespreekZaakKenmerk: {
				label: "Kenmerk van de bespreekzaak",
			},
			BetalingsmethodeAdvieskosten: {
				label: "Betalingsmethode advieskosten",
			},
			NettoAdvieskosten: {
				label: "Advieskosten",
			},
			BemiddelingsKosten: {
				label: "Bemiddelingskosten",
			},
			BoeteRente: {
				label: "Boeterente",
			},
			NHGJN: {
				label: "Is er sprake van NHG?",
			},
			MaatwerkOplossingJN: {
				label: "Is er sprake van een maatwerkoplossing?",
			},
			MaatwerkOplSpec: {
				label: "Maatwerkoplossing",
			},
			MaatwerkOplToelichting: {
				label: "Toelichting maatwerkoplossing",
			},
			HypotheekAkteKostenSpec: {
				label: "Kosten hypotheekakte",
			},
			NotarisKosten: {
				label: "Notariskosten",
			},
			NHGKostenSpec: {
				label: "NHG-kosten",
			},
			AankoopWoningJN: {
				label: "Wordt de woning aangekocht?",
			},
			BankgarantieBijAanbiederJN: {
				label: "Wordt de bankgarantie bij de aanbieder afgesloten?",
			},
			AflossingSVnStarterslening: {
				label: "Aflossing starterslening",
			},
			ExplainReden: {
				label: "Explainreden",
			},
			MaatwerkOplCodeMij: {
				label: "Code van de maatwerkoplossing",
			},
			CodeLeningDeel: {
				label: "Herkomstcode leningdeel",
			},
			BelastingBox: {
				label: "Belastingbox",
			},
			CodeDeelMij: {
				label: "Code van het leningdeel",
			},
			VasteEindDtLeningdeelJN: {
				label: "Is de einddatum van het leningdeel leidend?",
			},
			DuurInMnd: {
				label: "Duur in maanden",
			},
			EindDt: {
				label: "Einddatum",
			},
			LeningDeelBedrag: {
				label: "Leningdeelbedrag",
			},
			AflossingsVorm: {
				label: "Aflossingsvorm",
			},
			CodeRenteMij: {
				label: "Code van de rentevorm",
			},
			RenteAfspraak: {
				label: "Renteafspraak",
			},
			RenteVastInMnd: {
				label: "Rentevastperiode in maanden",
			},
			RenteBedenkTijd: {
				label: "Soort rentebedenktijd",
			},
			RenteBedenkTijdInMnd: {
				label: "Rentebedenktijd in maanden",
			},
			BetalingsTermijn: {
				label: "Betalingstermijn",
			},
			BetalingAchterafJN: {
				label: "Is de termijnbetaling achteraf?",
			},
			RentePct: {
				label: "Rentepercentage",
			},
			TekstRegel: {
				label: "Tekstregel 1",
			},
			TekstRegel2: {
				label: "Tekstregel 2",
			},
			TekstRegel3: {
				label: "Tekstregel 3",
			},
			TekstRegel4: {
				label: "Tekstregel 4",
			},
			TekstRegel5: {
				label: "Tekstregel 5",
			},
			IngangsDt: {
				label: "Ingangsdatum",
			},
			KvKNr: {
				label: "KVK-nummer",
			},
			DGAJN: {
				label: "Is de aanvrager een DGA?",
			},
			PctAandelen: {
				label: "Percentage aandelen",
			},
			AfgifteDt: {
				label: "Afgiftedatum",
			},
			RechtsVorm: {
				label: "Rechtsvorm",
			},
			BezittingenBoxDrie: {
				label: "Bezittingen box 3",
			},
			SoortBezittingBoxDrie: {
				label: "Soort bezitting box 3",
			},
			SoortBezittingBoxDrieOmschr: {
				label: "Omschrijving soort bezitting box 3",
			},
			WaardeBezittingBoxDrie: {
				label: "Waarde bezitting box 3",
			},
			SoortOverigeLeningOmschr: {
				label: "Omschrijving soort overige lening",
			},
			LeningLopendJN: {
				label: "Lopende lening?",
			},
			ServiceProvider: {
				label: "Serviceprovider",
			},
			// TODO: check if this is correct
			// ErfpachtPartJN: {
			//   label: "ErfpachtPartJN",
			// },
			VerzekeringNemer: {
				label: "Verzekeringnemer",
			},
			FinancieleDekking: {
				label: "Financiële dekking",
			},
			CodeFinancieleDekking: {
				label: "Code financiële dekking",
			},
			InlegBedrag: {
				label: "Inlegbedrag",
			},
			Box1IngangsDt: {
				label: "Box 1 ingangsdatum",
			},
			SubsidieRegeling: {
				label: "Subsidieregeling",
			},
			SubsidieRegelingBedrag: {
				label: "Bedrag",
			},
			ServiceProviderNaam: {
				label: "Naam",
			},
			ServiceProviderNr: {
				label: "Serviceprovidernummer",
			},
			ServiceProviderAFMRegistratieNr: {
				label: "AFM registratienummer",
			},
			Internetadres: {
				label: "Website",
			},
			VerblijfsVergunning: {
				label: "Verblijfsvergunning",
			},
			VerblijfsVergunningNr: {
				label: "Verblijfsvergunningnummer",
			},
			VerblijfsVergunningEindDt: {
				label: "Einddatum verblijfsvergunning",
			},
			LegitimatieSoort: {
				label: "Soort legitimatie",
			},
			RefPartijNAWData: {
				label: "NAW-gegevens",
			},
			RefContactPersoonData: {
				label: "Contactpersoon",
			},
			PlaatsNaamWerkgever: {
				label: "Plaatsnaam werkgever",
			},
			OnregelmatigheidsToeslag: {
				label: "Onregelmatigheidstoeslag",
			},
			AMSVerdiencapaciteit: {
				label: "Verdiencapaciteit",
			},
			AMSScore: {
				label: "Score",
			},
			AMSRapportNr: {
				label: "Rapportnummer",
			},
			DienstBetrekking: {
				label: "Dienstbetrekking",
			},
			SoortDienstBetrekking: {
				label: "Soort dienstbetrekking",
			},
			StraatNaamWerkgever: {
				label: "Straatnaam werkgever",
			},
			BrutoJaarSalaris: {
				label: "Bruto jaarsalaris",
			},
			HypotheekOpties: {
				label: "Hypotheekopties",
			},
			CodeHypotheekOptiesMij: {
				label: "Code hypotheekopties",
			},
			KadastraleGemeente: {
				label: "Gemeente",
			},
			Nr: {
				label: "Nummer",
			},
		},
		byType: {
			// AN100Type: {
			//   $formkit: "textarea",
			// },
			// AN255Type: {
			//   $formkit: "textarea",
			// },

			EnergieKlasseType: {
				sorted: true,
			},
			TussenPersoonEntiteitType: {
				label: "Tussenpersoon",
				props: { id: "tussenpersoon" },
			},
			FinancieleDekkingEntiteitType: {
				props: { id: "financiele-dekking" },
			},
			TekstRegelsEntiteitType: {
				label: "Tekstregels",
				props: {
					id: "tekstregels",
				},
			},
			OpleidingsNiveauType: {
				label: "Opleidingsniveau",
			},
			BurgerlijkeStaatType: {
				sorted: true,
				label: "Burgerlijke staat",
			},
			LeningEntiteitType: {
				props: {
					id: "lening",
				},
			},
			ObjectEntiteitType: {
				props: {
					id: "object",
				},
				fns: {
					instanceLabel: addressLabel,
				},
				fields: [
					"ObjectSoort",
					"BijzObjectSoort",
					"Onderpand",

					["StraatNaam", "HuisNr", "HuisNrToevoeging"],
					["Postcode", "PlaatsNaam", "Land"],

					"EigenBewoningJN",
					"HoofdVerblijfJN",

					"MarktWaarde",
					"BronWaardebepaling",
				],
			},
			KadastraleAanduidingEntiteitType: {
				fields: [["KadastraleGemeente", "Sectie", "Nr"], "Grootte"],
			},
			HuidigObjectEntiteitType: {
				label: { one: "Huidig object", many: "Huidige objecten" },
				fns: {
					instanceLabel: addressLabel,
				},
				props: {
					id: "huidig-object",
				},
				fields: [
					"ObjectSoort",
					"BijzObjectSoort",

					["StraatNaam", "HuisNr", "HuisNrToevoeging"],
					["Postcode", "PlaatsNaam", "Land"],

					"MarktWaarde",
					"BronWaardebepaling",

					"TeVerkopenJN",
					"VerkochtJN",
					"VerkoopakteJN",
					"VerkoopPrijs",
					"VerkoopDt",
					"VerkoopKosten",

					"HuidigeFinancieringJN",
					"AanvragerBestaandeHypotheek",
					"HuidigeFinanciering",
				],
			},
			HypotheekgeverEntiteitType: {
				label: { one: "Hypotheekgever", many: "Hypotheekgevers" },
				fns: {
					instanceLabel: (node: FormKitNode) => {
						const offerteAanvraag = node.parent?.parent;
						const instanceData = node.value as any;
						const partijNAWDataAll = (
							offerteAanvraag?.value as Record<string, unknown> | undefined
						)?.PartijNAWData;

						const partijNAWDataInstance = Array.isArray(partijNAWDataAll)
							? partijNAWDataAll.find(
									(partijNAWData) =>
										partijNAWData.ID &&
										partijNAWData.ID === instanceData.RefPartijNAWData?.IDREF,
								)
							: partijNAWDataAll;

						return labelFromPartijNAWData(partijNAWDataInstance);
					},
				},
				props: {
					id: "hypotheekgever",
				},
				fields: [
					"RefPartijNAWData",
					"E-mailadres",
					["TelefoonNrPrive", "TelefoonNrMobiel", "TelefoonNrWerk"],
					"BurgerlijkeStaat",
					"BurgerlijkeStaatDt",
					"HuwelijkOntbondenJN",
					"SoortOntbonden",
					"HuwelijkOntbondenDt",
					"WeduweWeduwnaarJN",
					"HoofdelijkAansprakelijkJN",
					"MedeSchuldenaarJN",
					"AanvragerIsStarterJN",
					"Eigenwoningreserve",
					"AfwijkendKlantProfielJN",
					"FiscaalInwonerVanNederlandJN",
					"UitsluitendOfMedeFiscaalInwonerBuitenNedJN",
				],
			},
			PartijNAWDataEntiteitType: {
				label: "NAW-gegevens",
				fns: {
					instanceLabel: (node: FormKitNode) => {
						const partijNAWData = node.value as any;
						return labelFromPartijNAWData(partijNAWData);
					},
				},
				props: {
					id: "partij-naw-data",
				},
				fields: [
					["VoorNamen", "GeboorteTussenvoegsels", "GeboorteAchternaam"],
					["VoorLetters", "VoorTitel"],
					"Nationaliteit",
					["GeboortePlaats", "GeboorteLand"],
					"GeboorteDt",
					[
						"CorrespondentieVoornaam",
						"CorrespondentieTussenvoegsels",
						"CorrespondentieNaam",
					],
					["StraatNaam", "HuisNr", "HuisNrToevoeging"],
					["Postcode", "PlaatsNaam", "Land"],
					"RekeningNr",
				],
			},
			NotarisEntiteitType: {
				label: {
					one: "Notaris",
					many: "Notarissen",
				},
				props: {
					id: "notaris",
				},
			},
			ID: {
				hidden: true,
			},
			HeaderEntiteitType: {
				omit: true,
			},
			LandType: {
				sorted: true,
			},
			BijzObjectSoortType: {
				sorted: true,
			},
			ObjectSoortType: {
				sorted: true,
			},
			DatumFieldType: {
				$formkit: "loanApplicationFormInputDate",
			},
			CodeLeningMijType: {
				sorted: true,
			},
			CodeDeelMijType: {
				sorted: true,
			},
			MaatschappijType: {
				sorted: true,
			},
			BeroepType: {
				sorted: true,
			},
			VoortitelType: {
				label: "Voortitel",
				sorted: true,
			},
		},
	});

	return { overrider };
}
